// Показателите на „Отстъпки и надбавки".
//
// Двата дяла („от всички сметки", „от брутото") искат числа, които ТАЗИ
// справка не получава — endpoint-ът връща само редовете с корекция. Идват от
// обобщението на „Справка сметки" и затова са НЕЗАДЪЛЖИТЕЛНИ: потребител без
// право `view_tables_history` вижда картите без дял, вместо измислена
// стойност или празен екран.

import { useTranslation } from "react-i18next";

import { fmtEuro } from "../../../../utils/currency";
import { ReportKpiCard } from "../ReportKpiCard";
import { deltaPercent } from "../reportDelta";
import { pluralLabel } from "../reportPlural";
import { APPROVAL_PERCENT } from "./discountsMath";
import type { DiscountsSummary } from "./discountsMath";

/** Минус знак U+2212 — отстъпката е отнета сума. */
const MINUS = "−";

type Props = {
  summary: DiscountsSummary;
  /** Предходният равен период — липсва ли, чиповете с Δ не се рисуват. */
  previous: DiscountsSummary | null;
  /** Всички закрити сметки за периода; `null` = няма право да ги види. */
  closedBills: number | null;
  /** Оборотът за периода (платените суми); `null` = няма право да го види. */
  periodRevenue: number | null;
  /** Показани са само сметките с отстъпка над прага. */
  isolatedAboveApproval: boolean;
  onToggleAboveApproval: () => void;
  isNarrow: boolean;
};

export const DiscountsKpis = ({
  summary,
  previous,
  closedBills,
  periodRevenue,
  isolatedAboveApproval,
  onToggleAboveApproval,
  isNarrow,
}: Props) => {
  const { t } = useTranslation();

  const billsShare =
    closedBills !== null && closedBills > 0
      ? (summary.discountBills / closedBills) * 100
      : null;

  // Брутото = платеното по ВСИЧКИ закрити сметки + отстъпките − надбавките,
  // тоест сумата преди корекциите. Делът отговаря на „колко от оборота е
  // раздадено", а не „колко от тези сметки".
  const gross =
    periodRevenue === null
      ? null
      : periodRevenue + summary.discountTotal - summary.surchargeTotal;
  const shareOfGross =
    gross !== null && gross > 0 ? (summary.discountTotal / gross) * 100 : null;

  const canIsolate = summary.aboveApproval > 0 || isolatedAboveApproval;

  return (
    // Класовете са ЦЕЛИ литерали: Tailwind сканира изходния текст и не вижда
    // име, сглобено с template literal.
    <div
      className={`grid gap-2.5 ${
        isNarrow
          ? "grid-cols-2"
          : "grid-cols-2 lg:grid-cols-[1.15fr_1.3fr_1fr_1fr_1fr]"
      }`}
    >
      <ReportKpiCard
        kicker={t("reportsPage.discountsKpiBills")}
        value={String(summary.discountBills)}
        sub={
          billsShare === null
            ? t("reportsPage.discountsKpiBillsUnit")
            : t("reportsPage.discountsKpiBillsShare", {
                share: billsShare.toFixed(1),
              })
        }
        delta={
          previous
            ? deltaPercent(summary.discountBills, previous.discountBills)
            : undefined
        }
        invertDelta
        hero
      />

      <ReportKpiCard
        kicker={t("reportsPage.discountsKpiGiven")}
        value={
          summary.discountTotal > 0
            ? `${MINUS}${fmtEuro(summary.discountTotal)}`
            : fmtEuro(0)
        }
        sub={
          shareOfGross === null
            ? t("reportsPage.discountsKpiGivenSub")
            : t("reportsPage.discountsKpiGivenShare", {
                share: shareOfGross.toFixed(2),
              })
        }
        delta={
          previous
            ? deltaPercent(summary.discountTotal, previous.discountTotal)
            : undefined
        }
        invertDelta
        hot={summary.discountTotal > 0}
        hero
      />

      <ReportKpiCard
        kicker={t("reportsPage.discountsKpiAverage")}
        value={fmtEuro(summary.averageDiscount)}
        sub={t("reportsPage.discountsKpiAverageSub")}
      />

      <ReportKpiCard
        kicker={t("reportsPage.discountsKpiAboveApproval", {
          percent: APPROVAL_PERCENT,
        })}
        value={String(summary.aboveApproval)}
        sub={
          isolatedAboveApproval
            ? t("reportsPage.discountsKpiAboveApprovalIsolated")
            : t("reportsPage.discountsKpiAboveApprovalSub")
        }
        // Кликът изолира точно тези сметки в списъка — иначе стигането до тях
        // минава през ръчен филтър по процент в колоната.
        onClick={canIsolate ? onToggleAboveApproval : undefined}
        hot={summary.aboveApproval > 0}
      />

      <ReportKpiCard
        kicker={t("reportsPage.discountsKpiSurcharges")}
        value={fmtEuro(summary.surchargeTotal)}
        sub={
          summary.surchargeBills === 0
            ? t("reportsPage.discountsKpiSurchargesNone")
            : pluralLabel(
                t,
                summary.surchargeBills,
                "reportsPage.discountsResultOne",
                "reportsPage.discountsResults",
              )
        }
      />
    </div>
  );
};
